import { createSelector, createSlice } from '@reduxjs/toolkit';
import LogRocket from 'logrocket';

import { STORAGE } from '@constants';
import { isValidServiceName, readServiceNameFromUrl } from '@helpers';

export const initialState = {
  activeServiceName: '',
  data: {},
  error: null,
  isAuthenticated: false,
  isAuthenticating: false,
  isBaseProxySwitched: false,
  isDataLoaded: false,
  isLoading: true,
};

const SERVICE_NAME_STORAGE_PREFIX = STORAGE.activeServiceName;

/** Reads the persisted active service name for a user (empty string if none / storage unavailable). */
const readStoredServiceName = (userId) => {
  if (!userId) return '';
  try {
    return localStorage.getItem(`${SERVICE_NAME_STORAGE_PREFIX}${userId}`) || '';
  } catch {
    return '';
  }
};

/** Persists the active service name for a user, keyed by user id. */
const storeServiceName = (userId, serviceName) => {
  if (!userId) return;
  try {
    localStorage.setItem(`${SERVICE_NAME_STORAGE_PREFIX}${userId}`, serviceName ?? '');
  } catch {
    /* storage unavailable (private mode / quota) — ignore */
  }
};

/** Resolves which service name should be active given the URL, the API payload and any stored preference. */
const resolveActiveServiceName = (payload) => {
  const serviceNames = payload?.serviceNames || [];
  const fromUrl = readServiceNameFromUrl();
  if (isValidServiceName(fromUrl, serviceNames)) return fromUrl;
  const stored = readStoredServiceName(payload?.id);
  if (isValidServiceName(stored, serviceNames)) return stored;
  return serviceNames[0] || '';
};

export const userSlice = createSlice({
  initialState,
  name: 'user',
  reducers: {
    login(state) {
      state.error = null;
      state.isAuthenticating = true;
    },
    loginFailed(state, action) {
      state.error = action.payload?.error;
      state.isAuthenticating = false;
    },
    loginSucceeded(state) {
      state.error = null;
      state.isAuthenticated = true;
      state.isAuthenticating = false;
    },

    logout: (state) => state,
    logoutFailed(state, action) {
      state.error = action.payload?.error;
    },
    logoutSucceeded(state) {
      state.isAuthenticated = false;
      state.isDataLoaded = false;
    },

    // Adopts a server-resolved service for this session only — unlike
    // setActiveServiceName it does NOT persist, so a backend default echoed on
    // a create page can't overwrite the user's stored preference.
    serviceNameResolved(state, action) {
      state.activeServiceName = action.payload || '';
    },

    setActiveServiceName(state, action) {
      state.activeServiceName = action.payload || '';
      storeServiceName(state.data?.id, action.payload);
    },

    switchToBaseUser(state) {
      state.isLoading = true;
    },
    switchToProxyUser(state) {
      state.isLoading = true;
    },
    switchUserBroadcastSucceeded(state) {
      state.isBaseProxySwitched = false;
    },
    switchUserFailed(state) {
      state.isLoading = false;
    },
    switchUserSucceeded(state) {
      state.isLoading = false;
      state.isBaseProxySwitched = true;
    },

    updatePersona: (state) => state,
    updatePersonaFailed(state, action) {
      state.error = action.payload.error;
    },
    updatePersonaSucceeded(state, action) {
      state.data = { ...state.data, ...action.payload };
    },

    updateSurveySharingPreference: (state) => state,
    updateSurveySharingPreferenceFailed(state, action) {
      state.error = action.payload?.error || action.payload;
    },
    updateSurveySharingPreferenceSucceeded(state, action) {
      state.data = { ...state.data, ...action.payload };
    },

    updateUserCreditsRemaining(state, action) {
      state.data.surveyInfo.userCredits = action.payload.userCreditsRemaining;
    },

    updateUserSubscriptionDetails(state, action) {
      if (!state.data.clientInfo?.subscriptionDetails) return;

      action.payload.forEach(({ subscriptionId, totalRemainingCompletes, totalUsedCompletes }) => {
        const subscription = state.data.clientInfo.subscriptionDetails.find(
          (sub) => sub.id === subscriptionId
        );
        if (subscription?.currentPeriod) {
          if (typeof totalUsedCompletes === 'number') {
            subscription.currentPeriod.usedCompletes = totalUsedCompletes;
          }
          if (typeof totalRemainingCompletes === 'number') {
            subscription.currentPeriod.remainingCompletes = totalRemainingCompletes;
          }
        }
      });
    },

    userFetch(state) {
      state.isAuthenticating = true;
    },
    userFetchFailed(state) {
      state.isAuthenticated = false;
      state.isAuthenticating = false;
      state.isDataLoaded = false;
    },
    userFetchSucceeded: (state, action) => {
      const payload = action.payload;
      const theme = localStorage.getItem(STORAGE.theme);

      if (import.meta.env.VITE_LOGROCKET) {
        LogRocket.identify(payload.id, {
          client: payload.clientInfo.name,
          clientId: payload.clientInfo.id,
          discipline: payload.persona.discipline,
          email: payload.email,
          industry: payload.persona.industry,
          jobTitle: payload.persona.jobTitle,
          name: `${payload.firstName} ${payload.lastName}`,
          proficiency: payload.persona.proficiency,
          size: payload.clientInfo.size,
          theme: JSON.parse(theme) || 'default',
        });
        LogRocket.track('User_Loaded', {
          engagementLevel: payload.engagementLevel,
        });
      }
      state.data = payload;
      state.activeServiceName = resolveActiveServiceName(payload);
      state.isAuthenticated = true;
      state.isAuthenticating = false;
      state.isDataLoaded = true;
      state.isLoading = false;
    },

    userUpdateEmailPreferencesFailed(state, action) {
      state.error = action.payload.error;
    },
    userUpdateEmailPreferencesRequested: (state) => state,
    userUpdateEmailPreferencesSucceeded: (state, action) => {
      state.data = { ...state.data, ...action.payload };
    },

    userUpdateFavoriteReport: (state, action) => {
      const { payload } = action;
      state.data.routes.forEach((route) => {
        if (route.id === 'insights-reports') {
          route.routes.forEach((subRoute) => {
            subRoute.routes.forEach((item) => {
              if (item.id === payload.id) {
                item.is_favorite = payload.isFavorite;
              }
            });
          });
        }
      });
    },
  },
});

export const {
  login,
  loginFailed,
  loginSucceeded,
  logout,
  logoutFailed,
  logoutSucceeded,
  serviceNameResolved,
  setActiveServiceName,
  switchToBaseUser,
  switchToProxyUser,
  switchUserBroadcastSucceeded,
  switchUserFailed,
  switchUserSucceeded,
  updatePersona,
  updatePersonaFailed,
  updatePersonaSucceeded,
  updateSurveySharingPreference,
  updateSurveySharingPreferenceFailed,
  updateSurveySharingPreferenceSucceeded,
  updateUserCreditsRemaining,
  updateUserSubscriptionDetails,
  userFetch,
  userFetchFailed,
  userFetchSucceeded,
  userUpdateEmailPreferencesFailed,
  userUpdateEmailPreferencesRequested,
  userUpdateEmailPreferencesSucceeded,
  userUpdateFavoriteReport,
} = userSlice.actions;

/**
 * Service-model context for the active user: the `count` of available service
 * names, the full `list`, and the `active` one. Memoized so `useSelector`
 * consumers do not re-render on unrelated user-state changes.
 */
// Stable sentinel so consumers get a referentially-identical `list` when the
// user has no services — otherwise every recomputation hands them a fresh `[]`
// and dependent effects/memos see a spurious change.
const EMPTY_SERVICE_NAMES = Object.freeze([]);

export const selectServiceContext = createSelector(
  (state) => state.user?.data?.serviceNames,
  (state) => state.user?.activeServiceName,
  (serviceNames, activeServiceName) => {
    const list = serviceNames || EMPTY_SERVICE_NAMES;
    return {
      active: activeServiceName || '',
      count: list.length,
      list,
    };
  }
);

export default userSlice.reducer;
